import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, SearchIcon } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';

type Row = {
  companyId: number;
  companyCode: string;
  companyName: string;
  accountId: number;
  accountCode: string;
  accountName: string;
  linkNumber: string;
  balance: number;
  balanceUSD: number;
};

export const IntercompanyReport: React.FC = () => {
  const { colors } = useTheme();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [run, setRun] = useState(false);

  const reportUrl =
    run
      ? buildReportQuery('/accounting/reports/intercompany', { asOfDate })
      : null;

  const { data, isLoading } = useSWR<{ rows: Row[] }>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'companyCode', header: 'Company' },
      { key: 'accountCode', header: 'IC Account', render: (_v, r) => r.accountName },
      { key: 'linkNumber', header: 'Link #' },
      { key: 'balance', header: 'Balance', align: 'right', render: formatReportAmount },
      { key: 'balanceUSD', header: 'USD', align: 'right', render: formatReportAmount },
      {
        key: 'stmt',
        header: '',
        render: (_v, row) => (
          <Link
            to={`/accounting/reports/link-statement?companyId=${row.companyId}&accountId=${row.accountId}&linkNumber=${encodeURIComponent(row.linkNumber)}&dateTo=${asOfDate}&dateFrom=2000-01-01`}
            className="text-xs text-indigo-600 hover:underline"
          >
            Statement
          </Link>
        ),
      },
    ],
    [asOfDate],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div>
          <FieldLabel>As of date</FieldLabel>
          <DateInput value={asOfDate} onChange={(e) => { setAsOfDate(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Button variant="default" onClick={() => setRun(true)} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Intercompany"
      exportFileBase="intercompany"
      exportDateTo={asOfDate}
      statusBadge={
        <Badge variant="info" className="font-normal">
          As of {formatDateGlobal(asOfDate)}
        </Badge>
      }
      recordCount={rows.length}
      readyMessage="Set as-of date and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => `${r.companyId}-${r.linkNumber}`}
        stickyHeader
        emptyMessage="No intercompany balances."
      />
    </AccountingReportShell>
  );
};
